import React, { useState } from "react";
import { Pressable, View } from "react-native";
import StyledText from "../../../common/ui/styled-text";
import { ChevronDown, ChevronRight } from "lucide-react-native";
import type {
  ISubscriptionFrontend,
  StripePaymentMethod,
} from "../../../../types/api/billing";
import BillingHistory from "./billing-history";
import EmbeddedCheckoutModal from "../embedded-checkout-modal";
import PaymentMethod, {
  extractPaymentMethodDetails,
  PaymentMethodIcon,
} from "../payment-method";
import { displayPrice } from "../../../../utils/price-helpers";
import { mapSubscriptionStatus } from "../../../../utils/stripe-status";
import clsx from "clsx";

const getSubscriptionPaymentMethod = (
  sub: ISubscriptionFrontend
): StripePaymentMethod | null => {
  if (sub.default_payment_method) {
    return sub.default_payment_method;
  } else if (sub.latest_invoice?.payment_intent?.payment_method) {
    return sub.latest_invoice.payment_intent.payment_method;
  }
  return null;
};

export interface SubscriptionListItemProps {
  sub: ISubscriptionFrontend;
}

const SubscriptionItem: React.FC<SubscriptionListItemProps> = ({ sub }) => {
  const [open, setOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const dpm = getSubscriptionPaymentMethod(sub);
  const { shortDisplay: pmShort, subtext: pmSubtext } =
    extractPaymentMethodDetails(dpm);

  // Latest invoice total (Stripe amounts are in the smallest currency unit, e.g., cents)
  const latestInvoice = sub.latest_invoice || undefined;
  const latestTotalCents: number | null =
    typeof latestInvoice?.total === "number" ? latestInvoice.total : null;

  return (
    <View className="border-b border-gray-200">
      <View className="flex-row items-center pl-1 pr-4 sm:pl-3 sm:pr-14 py-3 hover:bg-gray-50 transition-colors space-x-1 sm:space-x-2">
        <View className="w-[30px] items-center">
          <Pressable
            onPress={() => setOpen((v) => !v)}
            className="w-[28px] h-[28px] items-center justify-center rounded"
          >
            {open ? (
              <ChevronDown size={18} color="#111" />
            ) : (
              <ChevronRight size={18} color="#111" />
            )}
          </Pressable>
        </View>

        {/* Restaurant col (200px base, can shrink) */}
        <View className="min-w-[120px] basis-[200px] shrink">
          <StyledText
            className="text-sm font-medium text-gray-900"
            numberOfLines={1}
          >
            {sub.agentName || "(Unnamed Agent)"}
          </StyledText>
          <StyledText className="text-[11px] text-gray-500" numberOfLines={1}>
            {sub.id}
          </StyledText>
        </View>

        {/* Status col (small, fixed width) */}
        <View className="hidden sm:flex w-[60px] items-center">
          {(() => {
            const simple = mapSubscriptionStatus(sub.status);
            const isActive = simple === "active";
            const isError = simple === "error";
            const isPaused = simple === "paused";
            const isUnpaid = simple === "unpaid";
            const bg = clsx(
              isActive && "bg-green-50 border-green-200",
              isPaused && "bg-gray-50 border-gray-200",
              isError && "bg-red-50 border-red-200",
              isUnpaid && "bg-yellow-50 border-yellow-200"
            );
            const text = clsx(
              isActive && "text-green-700",
              isPaused && "text-gray-700",
              isError && "text-red-700",
              isUnpaid && "text-yellow-700"
            );
            const label = simple;
            return (
              <View className={`px-2 py-[2px] rounded-full border ${bg}`}>
                <StyledText className={`text-[11px] font-medium ${text}`}>
                  {label}
                </StyledText>
              </View>
            );
          })()}
        </View>

        {/* Flexible spacer after status to push right columns */}
        <View className="flex-1" />

        {/* Payment Method col */}
        <View className="min-w-[100px] basis-[140px] shrink items-end">
          <View className="flex-row items-center gap-1">
            <PaymentMethodIcon type={dpm?.type || "unknown"} color="#111" />
            <StyledText className="text-sm text-gray-900">
              {pmShort || "—"}
            </StyledText>
          </View>
          <StyledText className="text-[11px] text-gray-500" numberOfLines={1}>
            {pmSubtext || ""}
          </StyledText>
        </View>

        {/* Monthly Total col (latest invoice total) */}
        <View className="min-w-[80px] w-[100px] items-end">
          <StyledText className="text-sm font-semibold text-gray-900">
            {latestTotalCents != null ? displayPrice(latestTotalCents) : "—"}
          </StyledText>
        </View>
      </View>

      {/* Expanded content */}
      {open && (
        <View className="sm:px-3 pb-3 pt-2 bg-gray-50">
          <View className="px-4 sm:px-[30px] space-y-3">
            <PaymentMethod
              paymentMethod={dpm}
              onUpdate={() => setCheckoutOpen(true)}
            />
            {/* Checkout modal - ensure unmounted on close to reset state */}
            {checkoutOpen && (
              <EmbeddedCheckoutModal
                onClose={() => setCheckoutOpen(false)}
                subscriptionId={sub.id}
                customerId={sub.customer}
              />
            )}
            <BillingHistory invoices={sub.invoices ?? []} />
          </View>
        </View>
      )}
    </View>
  );
};

export default SubscriptionItem;

// Lightweight skeleton for a single subscription row; mirrors header layout
export const SubscriptionItemSkeleton: React.FC = () => {
  return (
    <View className="flex-row items-center pl-1 pr-4 sm:pl-3 sm:pr-14 py-3 space-x-1 sm:space-x-2 animate-pulse">
      <View className="w-[30px] items-center">
        <View className="w-6 h-6 rounded bg-gray-200" />
      </View>
      <View className="min-w-[120px] basis-[200px] shrink">
        <View className="h-4 w-24 rounded bg-gray-200 mb-1" />
        <View className="h-3 w-16 rounded bg-gray-100" />
      </View>
      <View className="hidden sm:flex w-[60px] items-center">
        <View className="h-5 w-12 rounded-full bg-gray-200" />
      </View>
      <View className="flex-1" />
      <View className="min-w-[100px] basis-[140px] shrink items-end">
        <View className="h-4 w-20 rounded bg-gray-200 mb-1" />
        <View className="h-3 w-16 rounded bg-gray-100" />
      </View>
      <View className="min-w-[80px] w-[100px] items-end">
        <View className="h-4 w-16 rounded bg-gray-200" />
      </View>
    </View>
  );
};
